import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, LogOut, RefreshCw, Mail, Inbox, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "aa_admin_token";

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function LoginCard({ onSuccess }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/verify`, {}, {
        headers: { "X-Admin-Token": pw },
      });
      localStorage.setItem(TOKEN_KEY, pw);
      toast.success("Welcome back, Amanda.");
      onSuccess(pw);
    } catch {
      toast.error("Wrong password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md bg-white border border-[#E5E0D8] p-10"
      >
        <div className="w-14 h-14 bg-navy text-cream flex items-center justify-center mb-6">
          <Lock size={22} />
        </div>
        <h1 className="font-display text-3xl md:text-4xl text-navy">
          Admin <span className="italic text-terracotta">access</span>
        </h1>
        <p className="mt-2 text-ink-muted">
          Enter your admin password to view subscribers and messages.
        </p>
        <input
          type="password"
          required
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="Admin password"
          data-testid="admin-password-input"
          className="w-full mt-7 px-4 py-3.5 bg-cream text-navy border-2 border-[#E5E0D8] focus:border-terracotta focus:outline-none text-base"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-button"
          className="btn-primary mt-5 w-full justify-center"
        >
          {loading ? "Verifying…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [verified, setVerified] = useState(false);
  const [tab, setTab] = useState("messages");
  const [subs, setSubs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [stats, setStats] = useState({ newsletter_count: 0, contact_count: 0 });
  const [loading, setLoading] = useState(false);

  const load = async (t = token) => {
    if (!t) return;
    setLoading(true);
    try {
      const h = { "X-Admin-Token": t };
      const [s, m, st] = await Promise.all([
        axios.get(`${API}/admin/newsletter`, { headers: h }),
        axios.get(`${API}/admin/contact`, { headers: h }),
        axios.get(`${API}/admin/stats`, { headers: h }),
      ]);
      setSubs(s.data);
      setMsgs(m.data);
      setStats(st.data);
      setVerified(true);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setVerified(false);
        toast.error("Session expired. Please sign in again.");
      } else {
        toast.error("Failed to load data.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setVerified(false);
  };

  const onLoginSuccess = (pw) => {
    setToken(pw);
    load(pw);
  };

  if (!verified || !token) {
    return <LoginCard onSuccess={onLoginSuccess} />;
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-cream pb-16">
      {/* Top bar */}
      <header className="bg-navy text-cream">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="font-display text-2xl">
            Amanda <span className="italic text-mustard">Abbott</span>
            <span className="ml-3 text-sm uppercase tracking-[0.2em] text-cream/60">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => load()}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 text-cream/85 hover:text-mustard transition-colors text-sm"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={signOut}
              data-testid="admin-signout"
              className="inline-flex items-center gap-2 text-cream/85 hover:text-terracotta transition-colors text-sm"
            >
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-10">
          <div data-testid="admin-stat-subscribers" className="bg-white border border-[#E5E0D8] p-7 flex items-center gap-5">
            <div className="w-14 h-14 bg-terracotta text-cream flex items-center justify-center">
              <Users size={22} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-ink-muted font-semibold">
                Newsletter subscribers
              </div>
              <div className="font-display text-4xl text-navy mt-1">{stats.newsletter_count}</div>
            </div>
          </div>
          <div data-testid="admin-stat-messages" className="bg-white border border-[#E5E0D8] p-7 flex items-center gap-5">
            <div className="w-14 h-14 bg-mustard text-navy flex items-center justify-center">
              <Inbox size={22} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-ink-muted font-semibold">
                Contact messages
              </div>
              <div className="font-display text-4xl text-navy mt-1">{stats.contact_count}</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#E5E0D8] mb-8">
          <button
            onClick={() => setTab("messages")}
            data-testid="admin-tab-messages"
            className={`px-5 py-3 text-sm uppercase tracking-[0.16em] font-semibold transition-colors ${
              tab === "messages"
                ? "text-navy border-b-2 border-terracotta -mb-px"
                : "text-ink-muted hover:text-navy"
            }`}
          >
            Messages ({msgs.length})
          </button>
          <button
            onClick={() => setTab("subscribers")}
            data-testid="admin-tab-subscribers"
            className={`px-5 py-3 text-sm uppercase tracking-[0.16em] font-semibold transition-colors ${
              tab === "subscribers"
                ? "text-navy border-b-2 border-terracotta -mb-px"
                : "text-ink-muted hover:text-navy"
            }`}
          >
            Subscribers ({subs.length})
          </button>
        </div>

        {/* Messages */}
        {tab === "messages" && (
          <div data-testid="admin-messages-list" className="space-y-4">
            {msgs.length === 0 && (
              <div className="bg-white border border-[#E5E0D8] p-10 text-center text-ink-muted">
                No contact messages yet.
              </div>
            )}
            {msgs.map((m) => (
              <article
                key={m.id}
                data-testid={`admin-message-${m.id}`}
                className="bg-white border border-[#E5E0D8] p-6 md:p-7"
              >
                <header className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div>
                    <h3 className="font-display text-xl text-navy">{m.name}{m.company ? <span className="text-ink-muted font-body text-base font-normal"> · {m.company}</span> : null}</h3>
                    <a
                      href={`mailto:${m.email}`}
                      className="text-sm text-terracotta hover:underline inline-flex items-center gap-1.5 mt-0.5"
                    >
                      <Mail size={13} /> {m.email}
                    </a>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] uppercase tracking-[0.16em] font-semibold bg-cream-alt text-navy px-2.5 py-1">
                      {m.topic}
                    </span>
                    {m.budget && (
                      <span className="text-[11px] uppercase tracking-[0.16em] font-semibold bg-mustard/25 text-navy px-2.5 py-1">
                        {m.budget}
                      </span>
                    )}
                    {m.timeline && (
                      <span className="text-[11px] uppercase tracking-[0.16em] font-semibold bg-terracotta/15 text-navy px-2.5 py-1">
                        {m.timeline}
                      </span>
                    )}
                    <span className="text-xs text-ink-muted">{fmtDate(m.received_at)}</span>
                  </div>
                </header>
                <p className="text-[15px] text-navy/85 leading-relaxed whitespace-pre-wrap">
                  {m.message}
                </p>
              </article>
            ))}
          </div>
        )}

        {/* Subscribers */}
        {tab === "subscribers" && (
          <div data-testid="admin-subscribers-list" className="bg-white border border-[#E5E0D8] overflow-hidden">
            {subs.length === 0 ? (
              <div className="p-10 text-center text-ink-muted">No subscribers yet.</div>
            ) : (
              <table className="w-full">
                <thead className="bg-cream-alt">
                  <tr>
                    <th className="text-left text-xs uppercase tracking-[0.16em] font-semibold text-navy px-6 py-4">Email</th>
                    <th className="text-left text-xs uppercase tracking-[0.16em] font-semibold text-navy px-6 py-4 hidden sm:table-cell">Subscribed</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.id} className="border-t border-[#E5E0D8]">
                      <td className="px-6 py-4">
                        <a href={`mailto:${s.email}`} className="text-navy hover:text-terracotta">{s.email}</a>
                      </td>
                      <td className="px-6 py-4 text-sm text-ink-muted hidden sm:table-cell">{fmtDate(s.subscribed_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {subs.length > 0 && (
              <div className="px-6 py-4 border-t border-[#E5E0D8] bg-cream-alt flex justify-end">
                <button
                  data-testid="admin-export-csv"
                  onClick={() => {
                    const csv = "email,subscribed_at\n" +
                      subs.map((s) => `${s.email},${s.subscribed_at}`).join("\n");
                    const blob = new Blob([csv], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url; a.download = "subscribers.csv"; a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="text-sm text-terracotta hover:text-navy font-semibold"
                >
                  Export CSV →
                </button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
